import { createContext, useContext } from 'react';

/**
 * WEB-703 (ратиф. PM 2026-07-17) — единый money-путь пейволла.
 *
 * До этого checkout-логика (lead-capture, трекинг, Stripe-сессия, редирект)
 * жила только внутри BuyNowButton. Кнопка даунселла (`downsell_popup` CTA) и
 * экспресс-кошельки не могли её вызвать → были мёртвыми. Теперь PaywallScreenWizard
 * реализует `startCheckout(priceId)` ОДИН раз и раздаёт через этот контекст;
 * BuyNowButton и downsell CTA ходят одной дорогой.
 *
 * `null` вне пейволла (квиз, editor-превью) → потребитель делает CTA инертным.
 */
// WEB-895: 'required-fields' — оплата заблокирована незаполненными
// обязательными блоками текущего экрана (downsell при этом не файрится).
// WEB-933 п.2: 'payments-unavailable' — сервер ответил кодом
// PAYMENTS_UNAVAILABLE, то есть проект сейчас физически не может принять
// деньги (платёжку не подключили или отключили уже после публикации).
// Свежий взгляд пачки 31, п.1: 'chunk-unavailable' — не удалось ЗАГРУЗИТЬ
// клиент чекаута (чанк пропал с CDN после выката фронта посреди сессии
// покупателя), то есть до сервера мы даже не дошли. Отличать от 'failed'
// обязательно: повторное нажатие тут не поможет никогда (браузер помнит
// провал модуля) — покупателю нужен другой совет, перезагрузить страницу.
// Свежий взгляд пачки 31, п.5: 'in-flight' — чекаут уже запущен другой
// дверью (кнопка в содержимом и её дубль в липкой полосе). Это НЕ отказ:
// показывать покупателю нечего, ждём исхода первого запроса.
export type CheckoutErrorCode =
  | 'preview'
  | 'no-price'
  | 'failed'
  | 'required-fields'
  | 'payments-unavailable'
  | 'chunk-unavailable'
  | 'in-flight';

/**
 * WEB-916 п.10: ЧТО именно не заполнено. Без этого кнопка не могла сказать
 * человеку «поставьте галочку» и показывала общий «Failed to start checkout».
 */
export type RequiredBlockKind = 'consent' | 'fields';

export interface CheckoutResult {
  ok: boolean;
  error?: CheckoutErrorCode;
  /**
   * Текст отказа как его прислал сервер. ПОКУПАТЕЛЮ НЕ ПОКАЗЫВАЕТСЯ
   * (WEB-1820): он написан на одном языке — русском, — а воронка может быть
   * на любом из тридцати одного. Поле остаётся для журналов и разбора.
   */
  message?: string;
  /**
   * WEB-1820: МАШИННЫЙ код отказа сервера (`{ code }` в теле ответа, его
   * сохраняет перехватчик `api/client.ts`). По нему публичная страница
   * выбирает текст из словаря ВОРОНКИ — см. `checkoutErrorText`. Ветвиться по
   * тексту нельзя: правка формулировки на сервере молча ломает подбор.
   */
  code?: string;
  /** Заполнено только при error === 'required-fields'. */
  requiredKind?: RequiredBlockKind;
}

export type StartCheckout = (
  priceId: string | undefined,
  opts?: {
    /** Показать downsell-попап (trigger `payment_declined`) при провале. По
     *  умолчанию true; сама кнопка даунселла передаёт false (иначе рекурсия). */
    fireDownsellOnFailure?: boolean;
  },
) => Promise<CheckoutResult>;

const PaywallCheckoutContext = createContext<StartCheckout | null>(null);

export const PaywallCheckoutProvider = PaywallCheckoutContext.Provider;

export function usePaywallCheckout(): StartCheckout | null {
  return useContext(PaywallCheckoutContext);
}
